import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { channelConnectionQuery, useUpdateAirbnbConnectionSettings } from '@/lib/channels';
import type { AirbnbChannelSettings } from '@/api/generated/types.gen';
import { messageFromError } from '@/lib/api';
import { useToast } from '@/components/ToastProvider';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

// Same options/defaults as the "connect new account" settings section in
// AirbnbConnect.tsx (kept local — this editor is small and self-contained).
const MIN_STAY_TYPE_OPTIONS = [
  { value: 'Arrival', label: 'Arrival' },
  { value: 'Through', label: 'Through' },
];

const BOOKING_AMOUNT_OPTIONS = [
  { value: 'Payout Amount', label: 'Payout Amount' },
  { value: 'Total Paid Amount', label: 'Total Paid Amount' },
];

type MinStayType = NonNullable<AirbnbChannelSettings['min_stay_type']>;
type BookingAmountSettings = NonNullable<AirbnbChannelSettings['booking_amount_settings']>;

function isMinStayType(v: unknown): v is MinStayType {
  return v === 'Arrival' || v === 'Through';
}

function isBookingAmountSettings(v: unknown): v is BookingAmountSettings {
  return v === 'Payout Amount' || v === 'Total Paid Amount';
}

interface Props {
  connectionId: string;
  canWrite: boolean;
}

/**
 * Editor for the Airbnb channel-level settings (Channex connection_link.settings)
 * on an ALREADY-connected Airbnb channel connection. Mirrors the "connect new
 * account" settings form in AirbnbConnect.tsx (same 5 fields, same
 * email-required-when-notifications-on rule), but reads/writes the live
 * connection's `settings` via GET/PUT /channel-connections/{id}(/settings)
 * instead of collecting values ahead of a fresh OAuth link.
 */
export function AirbnbSettingsEditor({ connectionId, canWrite }: Props) {
  const { toast } = useToast();
  const [emailError, setEmailError] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data: connection, isLoading } = useQuery(channelConnectionQuery(connectionId));

  const [minStayType, setMinStayType] = useState<MinStayType>('Arrival');
  const [bookingAmountSettings, setBookingAmountSettings] =
    useState<BookingAmountSettings>('Payout Amount');
  const [cohostPayoutCalculations, setCohostPayoutCalculations] = useState(false);
  const [sendEmailNotifications, setSendEmailNotifications] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState('');

  // Pre-fill the form from the connection's saved settings whenever it (re)loads —
  // e.g. first load, or after a successful save invalidates + refetches it.
  // Unknown/absent fields fall back to the same defaults as AirbnbConnect.tsx.
  useEffect(() => {
    if (!connection) return;
    const s = connection.settings as Record<string, unknown>;
    setMinStayType(isMinStayType(s.min_stay_type) ? s.min_stay_type : 'Arrival');
    setBookingAmountSettings(
      isBookingAmountSettings(s.booking_amount_settings) ? s.booking_amount_settings : 'Payout Amount',
    );
    setCohostPayoutCalculations(s.cohost_payout_calculations === true);
    setSendEmailNotifications(s.send_email_notifications === true);
    setNotificationEmail(typeof s.email === 'string' ? s.email : '');
    setEmailError(null);
  }, [connection]);

  const updateSettings = useUpdateAirbnbConnectionSettings();

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (sendEmailNotifications && !notificationEmail.trim()) {
      setEmailError('Email is required when email notifications are enabled.');
      return;
    }
    setEmailError(null);

    const body: AirbnbChannelSettings = {
      min_stay_type: minStayType,
      booking_amount_settings: bookingAmountSettings,
      cohost_payout_calculations: cohostPayoutCalculations,
      send_email_notifications: sendEmailNotifications,
    };
    if (sendEmailNotifications) {
      body.email = notificationEmail.trim();
    }

    try {
      await updateSettings.mutateAsync({ id: connectionId, body });
      toast({ kind: 'success', title: 'Airbnb channel settings saved' });
    } catch (saveErr) {
      setErr(messageFromError(saveErr));
    }
  }

  if (isLoading || !connection) return null;
  // Only show once the channel has actually completed connect — a connection
  // row can exist mid-OAuth (pending_auth, no Channex channel yet).
  if (connection.status === 'pending_auth' || !connection.channex_channel_id) return null;

  return (
    <div className="space-y-3 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3">
      <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
        Airbnb channel settings
      </h4>

      {canWrite ? (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label="Min stay type"
              options={MIN_STAY_TYPE_OPTIONS}
              value={minStayType}
              onChange={(e) => setMinStayType(e.target.value as MinStayType)}
            />
            <Select
              label="Booking amount"
              options={BOOKING_AMOUNT_OPTIONS}
              value={bookingAmountSettings}
              onChange={(e) =>
                setBookingAmountSettings(e.target.value as BookingAmountSettings)
              }
            />
          </div>

          <Checkbox
            label="Deduct co-host payout commission"
            checked={cohostPayoutCalculations}
            onChange={(e) => setCohostPayoutCalculations(e.target.checked)}
          />

          <Checkbox
            label="Send Airbnb booking email notifications"
            checked={sendEmailNotifications}
            onChange={(e) => {
              const checked = e.target.checked;
              setSendEmailNotifications(checked);
              if (!checked) setEmailError(null);
            }}
          />

          {sendEmailNotifications && (
            <Input
              label="Notification email"
              type="email"
              required
              value={notificationEmail}
              onChange={(e) => setNotificationEmail(e.target.value)}
              error={emailError ?? undefined}
            />
          )}

          {err && (
            <p role="alert" className="text-sm text-destructive">
              {err}
            </p>
          )}

          <Button type="submit" variant="primary" size="sm" loading={updateSettings.isPending}>
            Save channel settings
          </Button>
        </form>
      ) : (
        // Absent, not disabled: no editable controls, no Save button — just
        // the current values, same data a writer would see pre-filled above.
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-[0.06em] text-muted">Min stay type</dt>
            <dd>{minStayType}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-[0.06em] text-muted">Booking amount</dt>
            <dd>{bookingAmountSettings}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-[0.06em] text-muted">
              Co-host payout deduction
            </dt>
            <dd>{cohostPayoutCalculations ? 'Yes' : 'No'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-[0.06em] text-muted">
              Email notifications
            </dt>
            <dd>{sendEmailNotifications ? notificationEmail || 'Yes' : 'No'}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}
